import { CKButton, CKInputChips, CKTextFieldNext } from 'clearkit';
import { FC, useEffect, useState } from 'react';

import { useProfilePermissions } from '~/components/profile/useProfilePermissions';
import { ButtonContainer } from '~/components/unified/ButtonContainer';
import { SettingsCard } from '~/components/unified/SettingsCard';

import { APISettingsData } from './types';

type APISettingsProps = APISettingsData & {
  onChange: (value: APISettingsData) => void;
};

export const APISettings: FC<APISettingsProps> = ({
  onChange,
  webhookUrl: providedWebhookUrl,
  remoteAllowedReferrers: providedRemoteAllowedReferrers,
}) => {
  const [webhookUrl, setWebhookUrl] = useState<string>('');
  const [remoteAllowedReferrers, setRemoteAllowedReferrers] = useState<
    string[]
  >([]);
  const [isDirty, setIsDirty] = useState(false);

  const { isOwner } = useProfilePermissions();
  const isEditDisabled = !isOwner;

  useEffect(() => {
    setWebhookUrl(providedWebhookUrl ?? '');
    setRemoteAllowedReferrers(providedRemoteAllowedReferrers ?? []);
  }, [providedWebhookUrl, providedRemoteAllowedReferrers]);

  const handleSave = () => {
    onChange({ webhookUrl, remoteAllowedReferrers });
  };

  const handleDiscard = () => {
    setWebhookUrl(providedWebhookUrl ?? '');
    setRemoteAllowedReferrers(providedRemoteAllowedReferrers ?? []);
    setIsDirty(false);
  };

  const handleWebhookUrlChange = (value: string) => {
    setWebhookUrl(value);
    setIsDirty(true);
  };

  const handleRemoteAllowedReferrersChange = (value: string[]) => {
    setRemoteAllowedReferrers(value);
    setIsDirty(true);
  };

  return (
    <SettingsCard>
      <SettingsCard.Header>
        <SettingsCard.Heading>API Settings</SettingsCard.Heading>
      </SettingsCard.Header>
      <SettingsCard.Body className="grid gap-6">
        <CKTextFieldNext
          isDisabled={isEditDisabled}
          onChange={handleWebhookUrlChange}
          placeholder="https://example.com/webhook"
          value={webhookUrl}
        >
          <CKTextFieldNext.Label>Web Hook URL</CKTextFieldNext.Label>
        </CKTextFieldNext>
        <CKInputChips
          isDisabled={isEditDisabled}
          maxHeight="12rem"
          onChange={handleRemoteAllowedReferrersChange}
          placeholder={'*.example.com, example.com'}
          shouldOverflow={false}
          splitTokens={[',', ' ']}
          value={remoteAllowedReferrers}
        >
          <CKInputChips.Label>Allowed Referrers</CKInputChips.Label>
        </CKInputChips>
        <div className="text-xs">
          <strong>Pro tip:</strong> paste comma separated values or rows from a
          spreadsheet to add multiple domains at once.
        </div>
      </SettingsCard.Body>
      <SettingsCard.Footer className="flex justify-end">
        <ButtonContainer>
          <CKButton
            isDisabled={!isDirty || isEditDisabled}
            onClick={handleDiscard}
            variant="simple"
          >
            Discard changes
          </CKButton>
          <CKButton
            isDisabled={!isDirty || isEditDisabled}
            onClick={handleSave}
            variant="bold"
            variantColor="blue"
          >
            Save changes
          </CKButton>
        </ButtonContainer>
      </SettingsCard.Footer>
    </SettingsCard>
  );
};
